/* Your details, in a window your viewers do not get.
 *
 * The blur hides a field from the room and from the person typing into it equally, because a
 * page draws one set of pixels and the share carries those same pixels. There is one gap in
 * that: a share is of a surface, and a second browser window is a different surface. Share the
 * ProTrack tab, and a window sitting beside it is not in the capture at all.
 *
 * So this is the one place the two audiences genuinely come apart — not by rendering twice, but
 * by putting the readable copy somewhere the share was never pointed. The shared tab stays
 * blurred; this window is where the work actually happens. The boxes in here are real: type in
 * them and the blurred field on the page behind takes the same value, delete and it empties.
 *
 * The catch is the whole feature, and it is why the warning is not dismissible: if the person
 * picked "Entire Screen" rather than a tab or a window, this window is on the call in plain text
 * and they are worse off than they were with nothing. Nobody can tell from in here which they
 * picked — a page is never told it is being captured, let alone how — so the only honest thing
 * is to say so every time and let them check.
 *
 * Document Picture-in-Picture rather than a plain popup, because a popup drops behind the moment
 * you click back into the form you are filling in, which makes it useless for the one job it
 * has. PiP stays above everything. The price is that PiP cannot be minimised — always-on-top
 * and minimisable are the same switch, and staying on top is the half that matters here.
 *
 * Plain DOM rather than React: it is a form in a document we own outright, and a second React
 * root with its own stylesheet injection would be more machinery than the job needs. */

const WINDOW_NAME = 'protrack-your-details';
const REFRESH_MS = 300;

// Small enough to sit in a corner and be glanced at, wide enough for an IBAN on one line.
// Both kinds of window can be dragged and resized from here.
const W = 380;
const H = 440;
const FEATURES = `width=${W},height=${H},resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no,status=no`;

let win = null;
let timer = 0;
let fields = [];
let signature = '';
let hooks = { getHidden: () => true, onToggle: () => {} };

/* The label a person would recognise. autoPrivate flattens these to match patterns against;
 * here they have to stay readable, so this keeps the original text and only tidies the edges. */
function labelFor(el) {
    let node = el.parentElement;
    for (let up = 0; up < 2 && node; up += 1, node = node.parentElement) {
        if (node.querySelectorAll('input, textarea, select').length !== 1) break;
        const label = node.querySelector('label');
        if (label) {
            // Labels carry required-markers and parenthetical hints — "Phone Number *(required
            // for KYC)" — which are noise above the box they belong to.
            const text = label.textContent
                .replace(/\s+/g, ' ')
                .replace(/\*/g, '')
                .replace(/\s*\([^)]*\)\s*$/, '')
                .trim();
            if (text) return text;
        }
    }
    return el.placeholder || el.getAttribute('aria-label') || el.name || 'Field';
}

const isEditable = (el) => el.tagName === 'INPUT' || el.tagName === 'TEXTAREA';

function valueOf(el) {
    return isEditable(el) ? el.value : el.textContent.replace(/\s+/g, ' ').trim();
}

function collect() {
    return Array.from(document.querySelectorAll('[data-private]'))
        .filter((el) => isEditable(el) || el.textContent.trim());
}

/* React owns these inputs, and assigning to .value behind its back leaves its state stale — the
 * next render puts the old value straight back. Going through the prototype setter and firing
 * the event React actually listens for is what makes the change stick. */
function writeThrough(el, value) {
    const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value').set;
    setter.call(el, value);
    el.dispatchEvent(new Event('input', { bubbles: true }));
}

const STYLE = `
  :root { color-scheme: light; }
  * { box-sizing: border-box; }
  body { margin:0; font:14px/1.5 system-ui, -apple-system, "Segoe UI", "Helvetica Neue", sans-serif; background:#f9fafb; color:#111827; }
  header { padding:14px 16px 12px; border-bottom:1px solid #e5e7eb; background:#fff; display:flex; align-items:center; justify-content:space-between; gap:12px; }
  .brand { font-size:16px; font-weight:800; letter-spacing:-.3px; color:#111827; }
  .brand span { color:#2563eb; }
  header p { margin:2px 0 0; font-size:12px; color:#6b7280; }
  #toggle { flex:none; border:1px solid #bfdbfe; border-radius:8px; padding:7px 12px; font:600 12px system-ui; cursor:pointer; background:#eff6ff; color:#1d4ed8; }
  #toggle:hover { background:#dbeafe; }
  #toggle.off { background:#fffbeb; border-color:#fcd34d; color:#92400e; }
  .warn { margin:12px 16px 0; padding:10px 12px; border-radius:8px; background:#fffbeb; border:1px solid #fde68a; color:#92400e; font-size:12px; line-height:1.45; }
  .warn b { display:block; margin-bottom:2px; }
  form { padding:14px 16px 20px; display:grid; gap:14px; }
  label { display:block; font-size:12px; font-weight:500; color:#374151; margin-bottom:4px; }
  input, .ro { width:100%; padding:10px 12px; border:1px solid #d1d5db; border-radius:8px; font-size:14px; background:#fff; color:#111827; }
  input:focus { outline:none; box-shadow:0 0 0 2px #3b82f6; border-color:#3b82f6; }
  .ro { background:#f9fafb; word-break:break-all; }
  .empty { padding:24px 16px; color:#6b7280; font-size:13px; }
`;

function bodyHtml() {
    return `  <header>
    <div>
      <div class="brand">Pro<span>Track</span></div>
      <p>Your details. Type here and the blurred page fills in.</p>
    </div>
    <button id="toggle" type="button"></button>
  </header>
  <div class="warn">
    <b>This window is only private if you shared a tab or a window.</b>
    If you picked "Entire Screen" when you started sharing, everyone on the call can read this.
    Stop sharing and pick the ProTrack tab instead.
  </div>
  <form id="rows" autocomplete="off"></form>`;
}

/* PiP hands back a window with an empty document; a popup hands back about:blank, which also
 * has a head and a body. Same code fills either. */
function dress(target) {
    target.document.title = 'Your details';
    const style = target.document.createElement('style');
    style.textContent = STYLE;
    target.document.head.appendChild(style);
    target.document.body.innerHTML = bodyHtml();
}

function rebuild(doc, els) {
    const form = doc.getElementById('rows');
    if (!els.length) {
        form.innerHTML = '<p class="empty">Nothing hidden on the page you are looking at.</p>';
        return;
    }

    form.innerHTML = els.map((el, i) => {
        const label = labelFor(el);
        const body = isEditable(el)
            ? `<input id="f${i}" data-idx="${i}" type="text" spellcheck="false">`
            : `<div class="ro" id="f${i}"></div>`;
        return `<div><label for="f${i}">${label.replace(/[&<>"]/g, '')}</label>${body}</div>`;
    }).join('');

    // Typed in here, landed over there.
    form.querySelectorAll('input[data-idx]').forEach((mirror) => {
        mirror.addEventListener('input', () => {
            const target = fields[Number(mirror.dataset.idx)];
            if (target && target.isConnected) writeThrough(target, mirror.value);
        });
    });
}

function sync(doc, els) {
    els.forEach((el, i) => {
        const node = doc.getElementById(`f${i}`);
        if (!node) return;
        const value = valueOf(el);
        // Never overwrite the box somebody is typing in — that is how a caret jumps to the end
        // mid-word.
        if (node.tagName === 'INPUT') {
            if (doc.activeElement !== node && node.value !== value) node.value = value;
        } else if (node.textContent !== value) {
            node.textContent = value;
        }
    });

    const button = doc.getElementById('toggle');
    if (button) {
        const hidden = hooks.getHidden();
        const label = hidden ? 'Blurred on screen' : 'Showing on screen';
        if (button.textContent !== label) button.textContent = label;
        button.classList.toggle('off', !hidden);
    }
}

function render() {
    if (!win || win.closed) return stop();
    const doc = win.document;

    const els = collect();
    // Only rebuild when the set of fields changes — moving pages, or a section unfolding.
    const sig = els.map((el) => labelFor(el)).join('|');
    if (sig !== signature) {
        signature = sig;
        fields = els;
        rebuild(doc, els);
    } else {
        fields = els;
    }

    sync(doc, els);
}

export function isSideWindowOpen() {
    return Boolean(win && !win.closed);
}

export function stop() {
    clearInterval(timer);
    timer = 0;
    win = null;
    fields = [];
    signature = '';
}

export function closeSideWindow() {
    if (win && !win.closed) win.close();
    stop();
}

// Leaving it orphaned would be the worst outcome: a window full of somebody's bank details,
// still open, with nothing left driving it. Registered once, not once per open.
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', closeSideWindow);
}

/* Must be called straight off a click — a popup opened without a gesture behind it is blocked,
 * and the blocker gives nothing back that says so beyond the null return. */
export async function openSideWindow(opts = {}) {
    hooks = { getHidden: opts.getHidden || (() => true), onToggle: opts.onToggle || (() => {}) };

    if (isSideWindowOpen()) {
        win.focus();
        return { ok: true };
    }

    /* Always-on-top where the browser has it, which is the whole point: clicking back into the
     * form must not bury the window holding what goes in it. The request has to start inside
     * the click that triggered it or the browser refuses. */
    if (window.documentPictureInPicture?.requestWindow) {
        try {
            win = await window.documentPictureInPicture.requestWindow({ width: W, height: H });
        } catch {
            win = null; // dismissed, or no user activation left — fall through to a popup
        }
    }

    if (!win) {
        win = window.open('', WINDOW_NAME, FEATURES);
        if (!win) {
            win = null;
            return { ok: false, reason: 'blocked' };
        }
    }

    dress(win);

    // The control lives in here now, so that the shared tab carries no sign of it.
    win.document.getElementById('toggle').addEventListener('click', () => hooks.onToggle());
    win.addEventListener('pagehide', stop);

    signature = '';
    render();
    timer = setInterval(render, REFRESH_MS);

    return { ok: true };
}
